<script setup lang="ts">
import { toRefs, computed } from "vue";
import BaseDisplayRule from "@/modules/rule_engine/components/BaseDisplayRule.vue";
import { RuleSetRule } from "@/modules/rule_engine/ruleEngineClasses";
import DisplayConditions from "@/modules/rule_engine/components/conditions/DisplayConditions.vue";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import { displayColorFor } from "@/modules/rule_engine/ruleTypeColors";

const emit = defineEmits(["edit-rule", "delete-rule", "clone-rule"]);

const props = defineProps<{
	rule: RuleSetRule;
	index: number;
	readOnly: boolean;
}>();

defineOptions({ name: "DisplayLoadMyWheelsBasePriceRule" });

const { rule: groupRule, readOnly } = toRefs(props);

const componentIsSelected = computed(() => {
	return useRuleEnginedStore().ruleEngineState.selectedRules.includes(
		groupRule.value.uuid
	);
});

const editRule = () => {
	emit("edit-rule");
};

const cloneRule = () => {
	emit("clone-rule");
};

const deleteRule = () => {
	emit("delete-rule");
};
</script>

<template>
	<base-display-rule
		:color-string="
			!componentIsSelected ? displayColorFor(groupRule.type) : 'lime-lighten-5'
		"
	>
		<template v-if="!readOnly" v-slot:prepend>
			<v-checkbox
				color="primary"
				hide-details
				v-model="useRuleEnginedStore().ruleEngineState.selectedRules"
				:value="groupRule.uuid"
			></v-checkbox>
		</template>
		<template v-slot:display>
			<div>
				<div class="d-flex" style="height: 56px; align-items: center">
					<div>
						<v-card-title class="pl-0">Load MyWheels Base Price</v-card-title>
					</div>
					<v-spacer />
				</div>

				<v-divider class="mb-2" v-if="rule?.conditions?.length > 0 ?? false" />
				<display-conditions :conditions="rule?.conditions" />
			</div>
		</template>
		<template v-if="!readOnly" v-slot:actions>
			<v-btn
				@click="editRule"
				variant="text"
				icon
				size="small"
				density="comfortable"
				color="primary"
				v-tooltip="`Edit`"
			>
				<v-icon size="16">fa: fa-light fa-pen-to-square</v-icon>
			</v-btn>
			<v-btn
				@click="cloneRule"
				variant="text"
				icon
				size="small"
				density="comfortable"
				color="primary"
				v-tooltip="`Clone`"
			>
				<v-icon size="16">fa: fa-light fa-clone</v-icon>
			</v-btn>
			<v-btn
				@click="deleteRule"
				variant="text"
				icon
				size="small"
				density="comfortable"
				color="error"
				v-tooltip="`Delete`"
			>
				<v-icon size="16">fa: fa-light fa-trash-can</v-icon>
			</v-btn>
		</template>
	</base-display-rule>
</template>

<style scoped lang="scss">
:global(kbd) {
	--shade-01: hsla(0, 0%, 91%, 1);
	--shade-03: hsla(0, 0%, 75%, 1);

	background-color: var(--shade-01);
	border: 1px solid var(--shade-03);
	border-radius: 4px;
	box-shadow: 0 1px 1px var(--shade-03), 0 2px 0 0 var(--shade-01) inset;
	display: inline-block;
	font-size: 0.85em;
	font-weight: 700;
	padding-inline: 4px;
	white-space: nowrap;
	margin: 0 5px 0 5px;
}

.red {
	color: #e64a19;
}

.condition {
	border-left: 5px solid #1abc9c;
}

.value {
	font-weight: bold;
}
</style>
